import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ListChecks,
  FileText,
  Image,
  MessageSquare,
} from 'lucide-react';
import { format } from 'date-fns';
import { AmsAuditCheckpoint } from '../../../services/amsAuditCheckpointService';

interface CheckpointDetailCardProps {
  checkpoint: AmsAuditCheckpoint | null; // Full checkpoint data from API
  approvalStatus: {
    status: 'pending' | 'approved' | 'rejected' | 'not_required';
    approverComments?: string;
    approverId?: any;
    approvedAt?: string;
    rejectedAt?: string;
  };
  checkpointName: string; // Display name from approval data
  checkpointId: string;
  onApprove?: (checkpointId: string) => void;
  onReject?: (checkpointId: string) => void;
  showActions?: boolean; // Whether to show approve/reject buttons
  actionLoading?: boolean;
  type?: 'project' | 'asset'; // For styling differentiation
  assetName?: string; // For asset checkpoints
  selected?: boolean; // Whether this checkpoint is selected for a bulk action
  onToggleSelect?: (checkpointId: string) => void; // Only offered for pending checkpoints
}

const CheckpointDetailCard: React.FC<CheckpointDetailCardProps> = ({
  checkpoint,
  approvalStatus,
  checkpointName,
  checkpointId,
  onApprove,
  onReject,
  showActions = false,
  actionLoading = false,
  type = 'project',
  assetName,
  selected = false,
  onToggleSelect,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle size={12} /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
            <XCircle size={12} /> Rejected
          </span>
        );
      case 'pending':
      case 'not_required':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
            <Clock size={12} /> Pending
          </span>
        );
      default:
        return null;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  if (!checkpoint) {
    // Fallback view when checkpoint data is not available
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4 flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {onToggleSelect && approvalStatus.status === 'pending' && (
              <div className="mt-0.5 flex items-center justify-center h-5 w-5 -m-0.5 shrink-0">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(checkpointId)}
                  aria-label={`Select ${checkpointName} for bulk action`}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            <div className="mt-0.5">
              <ListChecks size={18} className={type === 'project' ? 'text-blue-600' : 'text-emerald-600'} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 leading-tight mb-2">
                {checkpointName}
              </h4>
              {!isExpanded && (
                <div className="flex flex-wrap items-start gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Status:</span>
                    <span>{getStatusBadge(approvalStatus.status)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronUp size={16} className="text-gray-400" />
            ) : (
              <ChevronDown size={16} className="text-gray-400" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg border shadow-sm transition-all duration-200 ${
        isExpanded
          ? 'border-blue-300 bg-blue-50/30'
          : 'border-gray-200 hover:bg-gray-50'
      }`}
    >
      {/* Header */}
      <div
        className="p-4 flex items-start justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3 flex-1">
          {onToggleSelect && approvalStatus.status === 'pending' && (
            <div className="mt-0.5 flex items-center justify-center h-5 w-5 -m-0.5 shrink-0">
              <input
                type="checkbox"
                checked={selected}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSelect(checkpointId)}
                aria-label={`Select ${checkpointName} for bulk action`}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="mt-0.5">
            {checkpoint.status === 'active' ? (
              <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
            ) : (
              <XCircle size={18} className="text-red-600 flex-shrink-0" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-900 leading-tight">
                {checkpointName}
              </h4>
              {assetName && type === 'asset' && (
                <span className="text-xs text-gray-500">({assetName})</span>
              )}
              <span className="ml-auto">{getStatusBadge(approvalStatus.status)}</span>
            </div>

            {/* Metadata - Only show Execution and Created by when collapsed */}
            {!isExpanded && (
              <div className="flex flex-wrap items-start gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-1">
                  <span className="font-medium">Execution:</span>
                  <span className="capitalize">{checkpoint.checkpointMeta.executionType}</span>
                </div>
                <div className="flex flex-col">
                  <span>
                    Created by{' '}
                    <span className="font-medium text-gray-700">
                      {typeof checkpoint.createdBy === 'object'
                        ? checkpoint.createdBy.name
                        : 'N/A'}
                    </span>
                  </span>
                  {checkpoint.createdAt && (
                    <span className="text-gray-400 mt-0.5">
                      {formatDateTime(checkpoint.createdAt)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="border-t border-gray-100 bg-white p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-6">
            {/* Approve/Reject Buttons */}
            {showActions && (onApprove || onReject) && (
              <div className="flex items-center justify-end gap-2 mb-4">
                {onApprove && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('Approve button clicked for checkpoint:', checkpointId);
                      onApprove(checkpointId);
                    }}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    <CheckCircle size={16} />
                    Approve
                  </button>
                )}
                {onReject && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('Reject button clicked in CheckpointDetailCard for checkpoint:', checkpointId);
                      onReject(checkpointId);
                    }}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border-2 border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    <XCircle size={16} />
                    Reject
                  </button>
                )}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Checkpoint Name
                </label>
                <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                  {checkpoint.name}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Execution Type
                </label>
                <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300 capitalize">
                  {checkpoint.checkpointMeta.executionType}
                </div>
              </div>
            </div>

            {/* Checkpoint Meta */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={checkpoint.checkpointMeta.documentApplicable}
                  disabled
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed"
                />
                <label className="ml-2 block text-sm font-semibold text-gray-700">
                  Document Applicable
                </label>
              </div>

              {checkpoint.checkpointMeta.documentApplicable &&
                checkpoint.checkpointMeta.documentTypes &&
                checkpoint.checkpointMeta.documentTypes.length > 0 && (
                  <div className="col-span-2 space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Document Types
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {checkpoint.checkpointMeta.documentTypes.map((docType, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm"
                        >
                          {docType}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={checkpoint.checkpointMeta.designedParameterAvailable}
                  disabled
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed"
                />
                <label className="ml-2 block text-sm font-semibold text-gray-700">
                  Designed Parameter Available
                </label>
              </div>

              {checkpoint.checkpointMeta.designedParameterAvailable && (
                <>
                  {checkpoint.checkpointMeta.designedParameterCategory && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Designed Parameter Category
                      </label>
                      <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                        {checkpoint.checkpointMeta.designedParameterCategory}
                      </div>
                    </div>
                  )}
                  {checkpoint.checkpointMeta.designedParameterUom && (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Designed Parameter UOM
                      </label>
                      <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                        {checkpoint.checkpointMeta.designedParameterUom}
                      </div>
                    </div>
                  )}
                  {checkpoint.checkpointMeta.actualParameter && (
                    <div className="col-span-2 space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Actual Parameter
                      </label>
                      <div className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-500 border-gray-300">
                        {checkpoint.checkpointMeta.actualParameter}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={checkpoint.checkpointMeta.imagesEnabled}
                  disabled
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed"
                />
                <label className="ml-2 block text-sm font-semibold text-gray-700">
                  Images Enabled
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={checkpoint.checkpointMeta.remarksEnabled}
                  disabled
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-not-allowed"
                />
                <label className="ml-2 block text-sm font-semibold text-gray-700">
                  Remarks Enabled
                </label>
              </div>
            </div>

            {/* Approval/Rejection Info */}
            {approvalStatus.approverComments && (
              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-xs font-medium text-yellow-800 mb-1">Rejection Comments:</p>
                <p className="text-xs text-yellow-700">{approvalStatus.approverComments}</p>
              </div>
            )}

            {approvalStatus.approverId && (
              <div className="text-xs text-gray-500">
                {approvalStatus.status === 'approved' && approvalStatus.approvedAt && (
                  <span>
                    Approved by{' '}
                    {typeof approvalStatus.approverId === 'object'
                      ? approvalStatus.approverId.name
                      : 'Approver'}{' '}
                    on {formatDateTime(approvalStatus.approvedAt)}
                  </span>
                )}
                {approvalStatus.status === 'rejected' && approvalStatus.rejectedAt && (
                  <span>
                    Rejected by{' '}
                    {typeof approvalStatus.approverId === 'object'
                      ? approvalStatus.approverId.name
                      : 'Approver'}{' '}
                    on {formatDateTime(approvalStatus.rejectedAt)}
                  </span>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CheckpointDetailCard;
